import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';
import { environment } from '../../environments/environment';
import { CostSource, CostSourceFilter } from '../components/cost-analysis/models/cost-analysis.models';

// ── Wire-format types ────────────────────────────────────────────────────────
/** Raw DTO returned by Finance.API GET /api/v1/monthly-cost/rolls. */
export interface RollMonthlyCostDto {
  id?: string;
  number: string;
  itemCode?: string;
  /**
   * Foreign key into LabDipRequest.Colors[]._id (Item.API). Null when the
   * roll is uncolored / pending. The cost API does not denormalize color
   * metadata — the UI resolves names + lab-dip numbers via a batch lookup.
   */
  colorReference?: string | null;
  productionCardKey?: EntityKey | null;
  knittingOrderKey?: EntityKey | null;
  workOrderKey?: EntityKey | null;
  year: number;
  month: number;
  exchangeRateHnlUsd: number;
  exchangeRateEurUsd: number;
  finalWeightLbs: number;
  areaCosts: AreaCostEntryDto[];
  totalCostUsd: number;
  costPerLbUsd: number;
  calculatedBy?: string | null;
  calculatedAt?: string | null;
}

export interface AreaCostEntryDto {
  costCenterCode: string;
  passNumber: number;
  date: string; // ISO date
  shift: number;
  weightLbs: number;
  costBreakdownUsd: Record<string, number>;
  totalCostUsd: number;
  /**
   * Real (period actuals) or Standard (synthesized from FabricColorStandardCost
   * for WIP rolls that haven't physically processed this area). Defaults to
   * Real for documents written before this field existed.
   */
  costSource: CostSource;
  /**
   * Per-key origin tag set by the read-time enricher when a cost component
   * was imputed from <c>FabricColorStandardCost</c>. Keys absent from this
   * dictionary default to <see cref="CostSource.Real"/>. Only present when
   * the entry is partial-mixed; fully-Real entries omit it entirely.
   */
  costBreakdownSource?: Record<string, CostSource> | null;
}

export interface EntityKey {
  reference: string;
  number: string;
  entityDefinitionCode: number;
}

export interface CostSummaryDto {
  groupKey: string;
  rollCount: number;
  totalLbs: number;
  costBreakdownUsd: Record<string, number>;
  totalCostUsd: number;
  costPerLbUsd: number;
}

/**
 * Numeric enum mirrors the C# enum on the wire (System.Text.Json default).
 * Switching to a string union would silently break comparisons against the
 * `status` field returned by /calculate.
 */
export enum MonthlyCostRunStatus {
  Success = 0,
  Failed = 1,
}

/**
 * Result of a per-area calculation. Failure modes that previously threw 5xx
 * now return 200 with status=Failed and a populated errorCode/errorMessage.
 * Callers must inspect `status` rather than relying on HTTP semantics.
 */
export interface MonthlyCostSummaryDto {
  rollsProcessed: number;
  rollsUpdated: number;
  costCenterCode: string;
  year: number;
  month: number;
  totalCostUsd: number;
  status: MonthlyCostRunStatus;
  errorCode?: string | null;
  errorMessage?: string | null;
}

export interface PrerequisiteCheckResult {
  canCalculate: boolean;
  missingItems: string[];
}

// ── Hierarchy DTOs (mirror Sierra.Finance.Models.DTOs.CostHierarchy*) ────────
/**
 * Pre-aggregated Fabric → Color → WorkOrder → PC/KO → Roll tree built
 * server-side. The dashboard renders this directly without any client-side
 * aggregation pass: backend computed once, ships ~500KB instead of the
 * multi-MB roll dump.
 */
export interface CostHierarchyResponse {
  year: number;
  month: number;
  fabrics: CostHierarchyNode[];
  excluded: { greige: number; yarn: number; unknown: number };
  /** Distinct color references referenced anywhere in the tree — drives the
   *  batch lookup against Item.API for the chip rendering. */
  colorReferences: string[];
  totals: PeriodKpiTotals;
}

export interface PeriodKpiTotals {
  rollCount: number;
  productionLbs: number;
  processedLbs: number;
}

export interface CostHierarchyNode {
  id: string;
  label: string;
  subLabel?: string | null;
  colorReference?: string | null;
  level: 'fabric' | 'color' | 'workOrder' | 'productionCard' | 'knittingOrder' | 'roll';
  totalLbs: number;
  totalCostUsd: number;
  costPerLbUsd: number;
  byProcess: Record<
    string,
    {
      totalCostUsd: number;
      totalLbs: number;
      costPerLbUsd: number;
      realTotalCostUsd: number;
      realTotalLbs: number;
      realCostPerLbUsd: number;
      standardTotalCostUsd: number;
      standardTotalLbs: number;
      standardCostPerLbUsd: number;
    }
  >;
  context: CostHierarchyContext;
  children: CostHierarchyNode[];
}

export interface CostHierarchyContext {
  fabricCode?: string | null;
  fabricDescription?: string | null;
  colorReference?: string | null;
  workOrderReference?: string | null;
  workOrderNumber?: string | null;
  productionCardReference?: string | null;
  productionCardNumber?: string | null;
  knittingOrderReference?: string | null;
  knittingOrderNumber?: string | null;
  rollNumber?: string | null;
}

/**
 * Body for the unified <c>POST /api/v1/monthly-cost/calculate</c> endpoint.
 *
 * The endpoint replaced the legacy split between <c>/calculate</c> (single
 * area, required <c>costCenterCode</c>) and <c>/calculate-all</c> (all six
 * areas). The server picks the run mode from <c>costCenterCodes</c>:
 *
 *   - <c>null</c> or empty → full month close (every registered area, plus
 *     the shared-MOI distribution post-pass and waste cross-area distribution).
 *   - non-empty list → only the listed areas; partial runs that skip required
 *     upstream areas are rejected with <c>BusinessException</c>.
 *
 * <c>offsetMinutes</c> is a query-string parameter on the API side, not
 * part of the body.
 */
export interface CalculateMonthlyCostCommand {
  /**
   * Areas to recalculate. <c>null</c> or empty triggers a full close.
   * Partial runs require every upstream area in the topology — the API
   * rejects partial runs that skip them.
   */
  costCenterCodes?: string[] | null;
  year: number;
  month: number;
  /** HNL → USD rate. Required by the API model binder. */
  exchangeRate: number;
  /** EUR → USD rate. Pass 0 when the period has no EUR-denominated invoice. */
  exchangeRateEurUsd: number;
}

// ── Service ──────────────────────────────────────────────────────────────────
@Injectable({ providedIn: 'root' })
export class MonthlyCostProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/monthly-cost';

  public getRolls(year: number, month: number, costCenterCode?: string): Observable<ApiResponse<RollMonthlyCostDto[]>> {
    let params = new HttpParams().set('year', year).set('month', month);
    if (costCenterCode) {
      params = params.set('costCenter', costCenterCode);
    }
    return this.httpClient.get<ApiResponse<RollMonthlyCostDto[]>>(`${this.baseUrl}/rolls`, { params });
  }

  public getRollByNumber(rollNumber: string, year: number, month: number): Observable<ApiResponse<RollMonthlyCostDto>> {
    const params = new HttpParams().set('year', year).set('month', month);
    return this.httpClient.get<ApiResponse<RollMonthlyCostDto>>(`${this.baseUrl}/rolls/${encodeURIComponent(rollNumber)}`, { params });
  }

  /**
   * Cross-area summary for a period. Optional <paramref name="costSource"/>
   * filter applies the Real / Standard split — omit for the combined view.
   */
  public getGeneralSummary(year: number, month: number, costSource?: CostSource): Observable<ApiResponse<CostSummaryDto>> {
    let params = new HttpParams();
    if (costSource !== undefined && costSource !== null) {
      params = params.set('costSource', costSource);
    }
    return this.httpClient.get<ApiResponse<CostSummaryDto>>(`${this.baseUrl}/summary/general/${year}/${month}`, { params });
  }

  /**
   * Per-area summary. <paramref name="costSource"/> follows the same
   * Real/Standard split as the general summary; omit for the combined view.
   */
  public getAreaSummary(
    costCenterCode: string,
    year: number,
    month: number,
    costSource?: CostSource,
  ): Observable<ApiResponse<CostSummaryDto>> {
    let params = new HttpParams();
    if (costSource !== undefined && costSource !== null) {
      params = params.set('costSource', costSource);
    }
    return this.httpClient.get<ApiResponse<CostSummaryDto>>(
      `${this.baseUrl}/summary/area/${encodeURIComponent(costCenterCode)}/${year}/${month}`,
      { params },
    );
  }

  /**
   * Run cost calculation. Pass an empty/null <c>costCenterCodes</c> for a
   * full close; pass a list to recalculate only those areas. Returns 200
   * with one entry per area run — inspect <c>status</c> on each entry for
   * partial failures.
   */
  public calculate(
    cmd: CalculateMonthlyCostCommand,
    options?: { offsetMinutes?: number },
  ): Observable<ApiResponse<MonthlyCostSummaryDto[]>> {
    let params = new HttpParams();
    if (options?.offsetMinutes !== undefined) {
      params = params.set('offsetMinutes', options.offsetMinutes);
    }
    return this.httpClient.post<ApiResponse<MonthlyCostSummaryDto[]>>(`${this.baseUrl}/calculate`, cmd, { params });
  }

  /**
   * Pre-aggregated Fabric → Color → WO → PC/KO → Roll tree for the period.
   * Replaces the heavy <c>getRolls</c> + client-side <c>FabricHierarchyService</c>
   * flow on the dashboard: the backend computes the hierarchy once and ships
   * ~500KB. Use <c>getRolls</c> only when per-roll details are needed
   * (e.g. the detail page's drill-down) and the cached state isn't populated.
   */
  public getHierarchy(year: number, month: number, costSource?: CostSource): Observable<ApiResponse<CostHierarchyResponse>> {
    let params = new HttpParams();
    if (costSource !== undefined && costSource !== null) {
      params = params.set('costSource', costSource);
    }
    return this.httpClient.get<ApiResponse<CostHierarchyResponse>>(`${this.baseUrl}/hierarchy/${year}/${month}`, { params });
  }

  /**
   * Check whether the engine has every input it needs to run for the given
   * (cost center, year, month). When `canCalculate` is false, `missingItems`
   * lists which inputs are missing (exchange rate, employee cost, utility
   * cost, asset depreciation, etc.).
   */
  public checkPrerequisites(costCenterCode: string, year: number, month: number): Observable<ApiResponse<PrerequisiteCheckResult>> {
    return this.httpClient.get<ApiResponse<PrerequisiteCheckResult>>(
      `${this.baseUrl}/${encodeURIComponent(costCenterCode)}/${year}/${month}/prerequisites`,
    );
  }

  public searchRolls(cmd: RollSearchCommand, pageNumber: number, pageSize: number): Observable<ApiResponse<RollSearchResult>> {
    const params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);
    const wire: RollSearchCommandWire = {
      ...cmd,
      costSource: cmd.costSource === 'Real' ? 1 : cmd.costSource === 'Standard' ? 2 : 0,
    };
    return this.httpClient.post<ApiResponse<RollSearchResult>>(`${this.baseUrl}/rolls/search`, wire, { params });
  }

  public downloadRollsExcel(cmd: RollSearchCommand): Observable<Blob> {
    const params = new HttpParams().set('format', 1);
    const wire: RollSearchCommandWire = {
      ...cmd,
      costSource: cmd.costSource === 'Real' ? 1 : cmd.costSource === 'Standard' ? 2 : 0,
    };
    return this.httpClient.post(`${this.baseUrl}/rolls/search`, wire, {
      params,
      responseType: 'blob',
    });
  }
}

export interface RollSearchCommand {
  year: number;
  month: number;
  costSource: CostSourceFilter;
  costCenterCodes?: string[] | null;
  fabricCodes?: string[] | null;
  colorReferences?: string[] | null;
  productionCardNumbers?: string[] | null;
  workOrderReferences?: string[] | null;
  rollNumbers?: string[] | null;
  customerReferences?: string[] | null;
  orderTypes?: number[] | null;
}

interface RollSearchCommandWire extends Omit<RollSearchCommand, 'costSource'> {
  costSource: number;
}

export interface RollSearchResult extends PaginatedApiResponse<RollMonthlyCostDto> {
  year: number;
  month: number;
  costSource: number;
}
